// Deal and pipeline management functions
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore'
import { getAuth } from 'firebase/auth'
import { db } from './config'

const DEALS_COLLECTION = 'deals'
const PIPELINES_COLLECTION = 'pipelines'

/**
 * Create a new deal
 * @param {Object} dealData - Deal data
 * @param {string} userId - Current user ID
 * @returns {Promise<string>} Document ID
 */
export const createDeal = async (dealData, userId) => {
  try {
    const authUser = getAuth().currentUser
    const ownerEmail = authUser?.email ?? null
    const docRef = await addDoc(collection(db, DEALS_COLLECTION), {
      ...dealData,
      owner: userId,
      ownerEmail,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
    return docRef.id
  } catch (error) {
    console.error('Error creating deal:', error)
    throw error
  }
}

/**
 * Update a deal
 * @param {string} dealId - Deal document ID
 * @param {Object} updates - Fields to update
 */
export const updateDeal = async (dealId, updates) => {
  try {
    const docRef = doc(db, DEALS_COLLECTION, dealId)
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    })
  } catch (error) {
    console.error('Error updating deal:', error)
    throw error
  }
}

/**
 * Update deal stage (for drag-and-drop)
 * @param {string} dealId - Deal document ID
 * @param {string} newStage - New stage name
 */
export const updateDealStage = async (dealId, newStage) => {
  return updateDeal(dealId, { stage: newStage })
}

/**
 * Delete a deal
 * @param {string} dealId - Deal document ID
 */
export const deleteDeal = async (dealId) => {
  try {
    await deleteDoc(doc(db, DEALS_COLLECTION, dealId))
  } catch (error) {
    console.error('Error deleting deal:', error)
    throw error
  }
}

/**
 * Get a single deal by ID
 * @param {string} dealId - Deal document ID
 * @returns {Promise<Object|null>} Deal data
 */
export const getDeal = async (dealId) => {
  try {
    const docRef = doc(db, DEALS_COLLECTION, dealId)
    const docSnap = await getDoc(docRef)
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() }
    }
    return null
  } catch (error) {
    console.error('Error getting deal:', error)
    throw error
  }
}

/**
 * Get all deals for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of deals
 */
export const getDeals = async (userId) => {
  try {
    const q = query(
      collection(db, DEALS_COLLECTION),
      where('owner', '==', userId),
      orderBy('createdAt', 'desc')
    )
    
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting deals:', error)
    throw error
  }
}

/**
 * Subscribe to deals changes (real-time)
 * @param {string} userId - User ID
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const subscribeToDeals = (userId, callback) => {
  const q = query(
    collection(db, DEALS_COLLECTION),
    where('owner', '==', userId),
    orderBy('createdAt', 'desc')
  )
  
  return onSnapshot(q, (snapshot) => {
    const deals = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
    callback(deals)
  }, (error) => {
    console.error('Error subscribing to deals:', error)
  })
}

/**
 * Subscribe to all deals (real-time). Firestore rules must allow reads — use only when the user has admin claim.
 * @param {Function} callback
 * @returns {Function} Unsubscribe function
 */
export const subscribeToAllDeals = (callback) => {
  const q = query(collection(db, DEALS_COLLECTION), orderBy('createdAt', 'desc'))

  return onSnapshot(q, (snapshot) => {
    const deals = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data()
    }))
    callback(deals)
  }, (error) => {
    console.error('Error subscribing to all deals:', error)
  })
}

/**
 * Get or create default pipeline for a user
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Pipeline data
 */
export const getOrCreatePipeline = async (userId) => {
  try {
    // Try to get existing pipeline
    const q = query(
      collection(db, PIPELINES_COLLECTION),
      where('owner', '==', userId)
    )
    
    const querySnapshot = await getDocs(q)
    
    if (!querySnapshot.empty) {
      const pipelineDoc = querySnapshot.docs[0]
      return { id: pipelineDoc.id, ...pipelineDoc.data() }
    }
    
    // Create default pipeline
    const defaultStages = [
      { id: 'lead', name: 'Lead', order: 0 },
      { id: 'qualified', name: 'Qualified', order: 1 },
      { id: 'proposal', name: 'Proposal', order: 2 },
      { id: 'negotiation', name: 'Negotiation', order: 3 },
      { id: 'won', name: 'Won', order: 4 },
      { id: 'lost', name: 'Lost', order: 5 }
    ]
    
    const docRef = await addDoc(collection(db, PIPELINES_COLLECTION), {
      owner: userId,
      stages: defaultStages,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
    
    return { id: docRef.id, stages: defaultStages, owner: userId }
  } catch (error) {
    console.error('Error getting/creating pipeline:', error)
    throw error
  }
}

